/**
 * Static Content
 * 
 * General static content used across the application.
 * Includes page titles, descriptions, and common text.
 */

import { BilingualText } from '@/types';

/**
 * Site metadata
 */
export const siteMetadata = {
  name: "FSHN INSTITUTE",
  tagline: {
    en: "Premium AI Fashion Prompts",
    es: "Prompts Premium de Moda AI",
  },
};

/**
 * Home page content
 */
export const homeContent = {
  hero: {
    tags: ["[AI]", "[Fashion]", "[Prompts]"],
    headline: {
      line1: {
        en: "Direct Your",
        es: "Dirige Tu",
      },
      connector: {
        en: "Next",
        es: "Próxima",
      },
      number: "",
      line2: {
        en: "Editorial",
        es: "Editorial",
      },
    },
    subtitle: {
      en: "Pro-grade fashion prompts that put you in the creative director's seat. Just add your AI tool.",
      es: "Prompts de moda profesionales que te ponen en el asiento del director creativo. Solo añade tu herramienta de IA.",
    },
    buttons: {
      browse: {
        en: "Browse Prompt Collections",
        es: "Ver Colecciones de Prompts",
      },
      learnMore: {
        en: "How It Works",
        es: "Cómo Funciona",
      },
    },
    scrollIndicator: {
      en: "Scroll",
      es: "Desliza",
    },
  },
  cta: {
    title: {
      en: "Ready to Create?",
      es: "¿Listo para Crear?",
    },
    subtitle: {
      en: "Get instant access to professional fashion prompts",
      es: "Obtén acceso instantáneo a prompts profesionales de moda",
    },
  },
};

/**
 * Shop page content
 */
export const shopContent = {
  title: {
    en: "Premium Prompt Collections",
    es: "Colecciones Premium de Prompts",
  },
  subtitle: {
    en: "Professional AI fashion prompts delivered instantly",
    es: "Prompts profesionales de moda AI entregados instantaneamente",
  },
  emptyState: {
    title: {
      en: "No products found",
      es: "No se encontraron productos",
    },
    subtitle: {
      en: "Tell me what product you'd like to create!",
      es: "¡Cuéntame qué producto te gustaría crear!",
    },
  },
  loading: {
    en: "Loading products...",
    es: "Cargando productos...",
  },
  error: {
    title: {
      en: "Failed to load products",
      es: "Error al cargar productos",
    },
  },
};

/**
 * Product page content
 */
export const productContent = {
  notFound: {
    title: {
      en: "Product not found",
      es: "Producto no encontrado",
    },
  },
  error: {
    title: {
      en: "Failed to load product",
      es: "Error al cargar el producto",
    },
  },
  addedToCart: {
    title: {
      en: "Added to cart!",
      es: "¡Añadido al carrito!",
    },
    description: (productTitle: string, lang: 'en' | 'es') => ({
      en: `${productTitle} has been added to your cart`,
      es: `${productTitle} ha sido añadido a tu carrito`,
    }[lang]),
  },
};

/**
 * Contact page content
 */
export const contactContent = {
  title: {
    en: "Get in Touch",
    es: "Contáctanos",
  },
  subtitle: {
    en: "Have questions? We're here to help.",
    es: "¿Tienes preguntas? Estamos aquí para ayudar.",
  },
  form: {
    name: {
      en: "Name",
      es: "Nombre",
    },
    namePlaceholder: {
      en: "Your name",
      es: "Tu nombre",
    },
    email: "Email",
    emailPlaceholder: {
      en: "you@example.com",
      es: "tu@ejemplo.com",
    },
    message: {
      en: "Message",
      es: "Mensaje",
    },
    messagePlaceholder: {
      en: "Tell us how we can help...",
      es: "Cuéntanos cómo podemos ayudar...",
    },
    submit: {
      en: "Send Message",
      es: "Enviar Mensaje",
    },
  },
  successMessage: {
    title: {
      en: "Message received!",
      es: "¡Mensaje recibido!",
    },
    description: {
      en: "We'll get back to you within 24 hours.",
      es: "Te responderemos en 24 horas.",
    },
  },
};

/**
 * FAQ page content
 */
export const faqContent = {
  title: {
    en: "Frequently Asked Questions",
    es: "Preguntas Frecuentes",
  },
  subtitle: {
    en: "Everything you need to know",
    es: "Todo lo que necesitas saber",
  },
};

/**
 * Success page content
 */
export const successContent = {
  title: {
    en: "Payment Successful!",
    es: "¡Pago Exitoso!",
  },
  subtitle: {
    en: "Thank you for your purchase. Check your email for the download link.",
    es: "Gracias por tu compra. Revisa tu email para el link de descarga.",
  },
  sending: {
    en: "Sending your prompts...",
    es: "Enviando tus prompts...",
  },
  sent: {
    en: "Download link sent to your email!",
    es: "¡Link de descarga enviado a tu email!",
  },
};

/**
 * Not Found (404) page content
 */
export const notFoundContent = {
  title: {
    en: "404 - Page Not Found",
    es: "404 - Página No Encontrada",
  },
  subtitle: {
    en: "The page you're looking for doesn't exist.",
    es: "La página que buscas no existe.",
  },
};

/**
 * Common UI text
 */
export const commonText = {
  buttons: {
    browsePromptPacks: {
      en: "Browse Collections",
      es: "Ver Colecciones",
    },
    learnMore: {
      en: "Learn More",
      es: "Aprende Más",
    },
    viewAllPacks: {
      en: "View All Collections",
      es: "Ver Todas las Colecciones",
    },
    addToCart: {
      en: "Add to Cart",
      es: "Añadir al Carrito",
    },
    backToShop: {
      en: "Back to Shop",
      es: "Volver a la Tienda",
    },
    backToHome: {
      en: "Back to Home",
      es: "Volver al Inicio",
    },
    tryAgain: {
      en: "Try Again",
      es: "Intentar de nuevo",
    },
  },
  navigation: {
    shop: {
      en: "Shop",
      es: "Tienda",
    },
    faq: "FAQ",
    contact: {
      en: "Contact",
      es: "Contacto",
    },
  },
  labels: {
    verified: {
      en: "Verified",
      es: "Verificado",
    },
  },
};

/**
 * Testimonials section content
 */
export const testimonialsContent = {
  title: {
    en: "What Creators Are Saying",
    es: "Lo Que Dicen Los Creadores",
  },
  subtitle: {
    en: "Real feedback from creators using our prompts.",
    es: "Comentarios reales de creadores que usan nuestros prompts.",
  },
};

/**
 * Mobile CTA bar content
 */
export const mobileCTAContent = {
  browseCollections: {
    en: "Browse Collections",
    es: "Ver Colecciones",
  },
  addToCart: {
    en: "Add to Cart",
    es: "Añadir",
  },
};

/**
 * Hero showcase cards content (floating cards in hero section)
 */
export const heroShowcaseCards = {
  card1: {
    icon: "📷",
    title: { en: "Includes: lighting + camera + styling + negative prompts", es: "Incluye: iluminación + cámara + estilismo + prompts negativos" },
    subtitle: { en: "", es: "" },
  },
  card2: {
    icon: "⚡",
    title: { en: "Instant download, straight to your inbox", es: "Descarga instantánea, directo a tu bandeja de entrada" },
    subtitle: { en: "", es: "" },
  },
  card3: {
    icon: "🛠️",
    title: { en: "Works with all major photo generation tools", es: "Funciona con todas las herramientas principales de generación de fotos" },
    subtitle: { en: "", es: "" },
  },
};
